import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { Estimate, EstimateStatus, EstimateVersion } from '../models/estimate.model';
import { Client, MakeOptional } from 'projects/common/src/public-api';
import { EstimateTab } from '../models/navigation.model';
import { BusinessService } from "./business.service";
import { UsersService } from "./users.service";
import { ViewAsService } from './view-as.service';
import { LobbyService } from './lobby.service';
import { DocType } from '../models/doc-type.model';
import { rpcFilter, SupabaseService } from "./supabase.service";
import { Note } from "../models/note.model";
import { Item } from "../models/item.model";
import { CommonEoIItem } from "../models/eoi.model";
import { MatLegacySnackBar } from "@angular/material/legacy-snack-bar";
import { frontToServerTranslation } from 'projects/common/src/lib/services/supabase.service';

export type TiledEstimate = {
  id: number,
  type: EstimateTab,
  createdAt: Date,
  name: string,
  opened: boolean,
  docType: DocType
};

export interface EstimateCreate {
  inCreatedBy: number;
  inClient: MakeOptional<Client, 'salesTaxPercentage'>;
  inWorkflowId?: number;
  inJobType?: string;
  inNotes?: Note[];
}

export interface EstimateRestore {
  inId: number;
  inVersionId: number;
  inVersionCreatedAt: Date;
  inCreatedBy: number;
  inClient: Client;
  inWorkflowId: number;
  inSignature?: boolean;
  inSurcharge?: boolean;
  inNote?: string;
  inDepositAmount?: number;
  inDepositPercent?: number;
  inDepositType?: 'percent' | 'amount';
  inDiscountType?: 'percent' | 'amount';
  inItems?: CommonEoIItem[];
}

export interface EstimateVersionCreate {
  inCreatedBy: number;
  inClient: MakeOptional<Client, 'salesTaxPercentage'> & { salesTaxPercentage?: number };
  inEstimateId: number;
  inWorkflowId?: number;
  inSignature?: boolean;
  inSurcharge?: boolean;
  inNote?: string;
  inDepositAmount?: number;
  inDepositPercent?: number;
  inDepositType?: 'percent' | 'amount';
  inDiscountType?: 'percent' | 'amount';
  inItems?: Item[];
  inId?: number;
  inCreatedAt?: Date;
}

export interface EstimateVersionRestore {
  inId: number;
  inVersionId: number;
  inVersionCreatedAt: Date;
  inCreatedBy: number;
  inClient: MakeOptional<Client, 'salesTaxPercentage'> & { salesTaxPercentage?: number };
  inWorkflowId: number;
  inSignature?: boolean;
  inSurcharge?: boolean;
  inNote?: string;
  inDepositAmount?: number;
  inDepositPercent?: number;
  inDepositType?: 'percent' | 'amount';
  inDiscountType?: 'percent' | 'amount';
  inItems?: CommonEoIItem[];
}

interface EstimateUpdate {
  deletedAt: Date | null;
  activeDate: Date | null;
  activeUserId: number;
  activeOpened: boolean;
  activeStatus: EstimateStatus;
  activeVersionId: number;
  type: 'personal' | 'business';
  accepted: boolean;
  canceledFromPayment: boolean;
}

export function tiledEstimatesForType(type: EstimateTab) {
  return function filterSent(source$: Observable<Estimate[]>): Observable<TiledEstimate[]> {
    return source$.pipe(
      map((estimate) => {
        const category: TiledEstimate[] = [];

        // estimate.forEach(async estimate => {
        //   category.push({
        //     id: estimate.id,
        //     type: type,
        //     createdAt: estimate.activeCreatedAt!,
        //     name: estimate.versions[estimate.activeVersionId!].name,
        //     opened: estimate.versions[estimate.activeVersionId!].opened ?? false,
        //     docType: 'Estimate'
        //   });
        // });

        return category;
      })
    );
  }
}

@Injectable({
  providedIn: 'root'
})
export class EstimatesService {

  _recalculate$ = new Subject<0>();
  recalculate$ = this._recalculate$.asObservable().pipe(debounceTime(1000));


  constructor(
    private usersServices: UsersService,
    private supabaseService: SupabaseService,
    private lobbyService: LobbyService,
    protected businessService: BusinessService,
    protected viewAsService: ViewAsService,
    protected snackbar: MatLegacySnackBar,
  ) {}

  estimateObservable(estimateId: number, workflowId: number) {
    return this.businessService.selectedBusiness$.pipe(
      switchMap(business => this.supabaseService.rpc<Estimate[]>({
          cName: 'estimate_' + estimateId,
          schema: business!.businessId,
          fn: 'get_estimate_by_id',
          tables: [
            { table: 'estimate', filter: rpcFilter('id', 'eq', estimateId) },
            { table: 'estimate_version', filter: rpcFilter('estimate_id', 'eq', estimateId) },
            { table: 'estimate_version_client', filter: rpcFilter('estimate_id', 'eq', estimateId) },
            { table: 'client_estimate', filter: rpcFilter('estimate_id', 'eq', estimateId) },
            { table: 'workflow', filter: rpcFilter('id', 'eq', workflowId) },
            { table: 'payment', filter: rpcFilter('workflow_id', 'eq', workflowId) },
            { table: 'paid', filter: rpcFilter('workflow_id', 'eq', workflowId) },
            { table: 'refunded', filter: rpcFilter('workflow_id', 'eq', workflowId) },
            { table: 'user' },
          ],
          options: { in_id: estimateId }
        },
        (changes, items) => {
          if (!(changes.table === 'workflow' && changes.eventType === 'UPDATE'))
            return null;
          items[0].jobType = (changes.new as any).jobType;
          return items;
        }
      )),
      map(estimates => {
        if(!estimates || !estimates[0])
          return null;
        const estimate = estimates[0];
        estimate.docType = 'Estimate';
        estimate.versions.sort((a, b) => a.id - b.id)
        return estimate;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  async sendEstimate(estimateVersionId: number) {
    const currentUser = await this.usersServices.currentUser$.pipe(take(1)).toPromise();
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    const res: string = await this.supabaseService.rpcFunc(business!.businessId, 'send_estimate', {
      in_estimate_version_id: estimateVersionId,
      in_sending_user_id: currentUser.id
    });

    this.sendEstimatelEmail(res, currentUser.id).then();

    console.log('client estimate id: ', res);

    return res;
  }

  async sendEstimatelEmail(clientEstimateId: string, senderId: number) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    this.supabaseService.edgeFunc('send-estimate-email', {
      businessId: business!.businessId,
      clientEstimateId: clientEstimateId,
      senderId: senderId
    }).then();
  }

  async createEstimate(data: EstimateCreate) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return await this.supabaseService.rpcFunc<{ workflowId: number, estimateId: number }>(business!.businessId, 'create_estimate', data);
  }

  async updateEstimate(estimateId: number, data: EstimateUpdate) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return this.supabaseService.update(business!.businessId, 'estimate', estimateId, data);
  }

  async restoreEstimate(data: EstimateRestore) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return this.supabaseService.rpcFunc<number>(business!.businessId, 'restore_estimate', data);
  }

  async deleteEstimateVersion(id: number) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return this.supabaseService.rpcFunc<boolean>(business!.businessId, 'delete_estimate_version', {
      in_estimate_version_id: id
    });
  }

  async restoreEstimateVersion(data: EstimateVersionRestore) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    return (await this.supabaseService.supabase
        .schema(business!.businessId)
        .rpc('restore_estimate', frontToServerTranslation(data))).data;
  }

  findHighestVersionId(versions: EstimateVersion[], notIn?: number[]): number {
    return versions?.reduce((max, version) => {
      return version.id > max && !(notIn?.find(x => x === version.id)) ? version.id : max;
    }, -1);
  }

  findHighestDominantVersionId(versions: EstimateVersion[]): number {
    const dominantOrder: EstimateStatus[] = ['sent', 'accepted', 'declined', 'canceled', 'canceledFromPayment', 'created', 'deleted'];
    let dominantVersionId: number = -1;

    for (let status of dominantOrder) {
      if (dominantVersionId === -1)
        dominantVersionId = versions?.reduce((max, version) => {
          return version.id > max && version.status === status ? version.id : max;
        }, -1);
      else
        break;
    }

    return dominantVersionId;
  }

  async createEstimateVersion(data: EstimateVersionCreate) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return this.supabaseService.rpcFunc<number>(business!.businessId, 'create_estimate_version', data);
  }

  async duplicateEstimateVersion(estimate: Estimate, estimateVersion: EstimateVersion): Promise<number> {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const user = await this.usersServices.currentUser$.pipe(take(1)).toPromise();
    const data = {
      inClient: {
        address: estimateVersion.clientAddress,
        businessName: estimateVersion.clientBusinessName,
        email: estimateVersion.clientEmail,
        extNumber: estimateVersion.clientExtNumber,
        firstName: estimateVersion.clientFirstName,
        lastName: estimateVersion.clientLastName,
        phoneNumber: estimateVersion.clientPhoneNumber,
        salesTaxPercentage: estimateVersion.clientSalesTaxPercentage!,
        type: estimateVersion.clientType,
        unit: estimateVersion.clientUnit,
      },
      inCreatedBy: user.id,
      inEstimateId: estimate.id,
      inWorkflowId: estimate.workflowId,
      inSignature: estimateVersion.signature,
      inSurcharge: estimateVersion.surcharge,
      inNote: estimateVersion.note,
      inDepositAmount: estimateVersion.depositAmount,
      inDepositPercent: estimateVersion.depositPercent,
      inDepositType: estimateVersion.depositType,
      inDiscountType: estimateVersion.discountType,
      inItems: estimateVersion.items,
    };
    return this.supabaseService.rpcFunc<number>(business!.businessId, 'create_estimate_version', data);
  }

  async updateEstimateVersionClient(
    estimateVersionId: number,
    client: Client
  ) {
    delete (client as any).jobType;
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return this.supabaseService.update(
      business!.businessId,
      'estimate_version_client',
      {
        key: 'estimate_version_id',
        value: estimateVersionId
      },
      client
    );
  }

  async updateEstimateNote(
    estimateVersionId: number,
    estimatesNoteText: string
  ){
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    await this.supabaseService.supabase
      .schema(business!.businessId)
      .from('estimate_version')
      .update({ note: estimatesNoteText })
      .eq('id', estimateVersionId);
  }

  async updateEstimateVersion(
    id: number,
    estimateVersion: Partial<EstimateVersion>,
  ) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return this.supabaseService.update(business!.businessId, 'estimate_version', id, estimateVersion);
  }

  async declineEstimate(versionId: number) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    return await this.supabaseService.rpcFunc(business!.businessId, 'decline_estimate', {
      in_estimate_version_id: versionId
    });
  }

  async cleanUpEstimate(estimateId: number) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();

    this.supabaseService.rpcFunc<number>(business!.businessId, 'clean_up_estimate', {
      inEstimateId: estimateId
    }).then();
  }

  async deleteEstimate(id: number) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    await this.supabaseService.rpcFunc(business!.businessId, 'delete_estimate', { inId: id });
  }
  
  async getEstimate(id: number) {
    const business = await this.businessService.selectedBusiness$.pipe(take(1)).toPromise();
    const estimate = (await this.supabaseService.rpcFunc<Estimate[]>(business!.businessId, 'get_estimate_by_id', { inId: id }))[0];
    estimate.docType = 'Estimate';
    return estimate;
  }

}
